/**
 * O DESIGN SYSTEM DO NECTUNO (20/10, pedido do Pedro: "para não ficar criando
 * sempre componentes novos; quero que seja tudo reutilizável").
 *
 * Cada peça aqui é a que o sistema já usa, com o desenho que foi escolhido nas
 * rodadas de 19 e 20/10 — a linha fina nos campos, o trilho de texto nas
 * escolhas, o cartão de superfície sólida, a pílula de estado. Tela nova (ou
 * peça nova numa tela) usa daqui; só se nada servir é que se cria outra, e
 * ela entra AQUI, com o exemplo na página `/dev/design`.
 *
 * As cores, os raios e as letras vêm dos tokens (`estilos/tokens.css`): nada
 * aqui escreve uma cor, e por isso tudo funciona nos dois temas.
 *
 * O catálogo vivo, com cada componente em cada variante e o código de uso, é
 * a rota `/dev/design` — só no `npm run dev`, nunca em produção.
 */
import { useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { IconeAlerta, IconeChevron, IconeInfo, IconeLupa } from "./icones";
import css from "./ds.module.css";

export * from "./icones";

const junta = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

/* ═══ BOTÕES ════════════════════════════════════════════════════════════════ */

export type VarianteBotao = "primario" | "secundario" | "perigo" | "texto" | "discreto";

/**
 * O botão. `primario` é A ação da tela (azul cheio, uma por área); `secundario`
 * a alternativa (borda); `perigo` o que apaga; `texto` a ação sem caixa, em
 * azul ("Limpar filtros", "Mostrar mais"); `discreto` a que fica de lado até o
 * mouse chegar — cinza e miúda, azul no hover (o "Limpar tudo" dos filtros).
 */
export function Botao({
  variante = "secundario",
  pequeno,
  icone,
  iconeDepois,
  className,
  children,
  ...resto
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: VarianteBotao;
  pequeno?: boolean;
  /** Ícone antes do texto. */
  icone?: ReactNode;
  /** Ícone depois do texto (a seta de "seguir"). */
  iconeDepois?: ReactNode;
}) {
  return (
    <button
      type="button"
      className={junta(css.botao, css[`botao_${variante}`], pequeno && css.pequeno, className)}
      {...resto}
    >
      {icone}
      {children}
      {iconeDepois}
    </button>
  );
}

/**
 * O botão só de ícone. O `rotulo` é obrigatório: vira o `aria-label` e o
 * `title` — sem ele o botão não tem nome para o leitor de tela nem para o
 * mouse. `ativo` marca o escolhido (azul e um ponto embaixo), como na troca
 * Lista / Por linha.
 */
export function BotaoIcone({
  rotulo,
  ativo,
  redondo,
  pequeno,
  perigo,
  className,
  children,
  ...resto
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  rotulo: string;
  ativo?: boolean;
  /** O círculo azul cheio (o "buscar" da Home). */
  redondo?: boolean;
  /** 32px, também no toque: dentro de um cartão pequeno (o excluir da palavra). */
  pequeno?: boolean;
  /** O que apaga: fica vermelho no mouse e enquanto a confirmação está aberta
   * (`aria-expanded`). */
  perigo?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      title={rotulo}
      aria-pressed={ativo}
      className={junta(css.botaoIcone, redondo && css.botaoRedondo, pequeno && css.botaoIconePequeno,
                       perigo && css.botaoIconePerigo, className)}
      {...resto}
    >
      {children}
    </button>
  );
}

/* ═══ CAMPOS (a linha fina) ═════════════════════════════════════════════════ */

/**
 * O campo de texto na LINHA FINA: sem caixa, o fio embaixo, que acende azul no
 * foco; o rótulo pequeno em cima. `icone` vai encostado à esquerda (a lupa da
 * busca).
 */
export function Campo({
  rotulo,
  icone,
  className,
  id,
  ...resto
}: InputHTMLAttributes<HTMLInputElement> & { rotulo?: string; icone?: ReactNode }) {
  const proprio = useId();
  const chave = id ?? proprio;
  return (
    <label className={junta(css.campo, className)} htmlFor={chave} data-tipo={resto.type ?? "text"}>
      {rotulo && <span className={css.rotulo}>{rotulo}</span>}
      <span className={css.campoLinha}>
        {icone && <span className={css.campoIcone}>{icone}</span>}
        <input id={chave} {...resto} />
      </span>
    </label>
  );
}

/** A busca: o campo com a lupa, de até 320px (a largura toda no celular). O
 * rótulo, se não aparece, vai para o leitor de tela. */
export function Busca({
  rotulo = "Buscar",
  rotuloVisivel,
  className,
  ...resto
}: InputHTMLAttributes<HTMLInputElement> & { rotulo?: string; rotuloVisivel?: boolean }) {
  return (
    <Campo
      type="search"
      icone={<IconeLupa />}
      rotulo={rotuloVisivel ? rotulo : undefined}
      aria-label={rotuloVisivel ? undefined : rotulo}
      className={junta(css.busca, className)}
      {...resto}
    />
  );
}

export interface Opcao<T extends string = string> {
  valor: T;
  rotulo: string;
  /** O número ao lado (contagem). */
  conta?: number;
  /** O que a opção quer dizer, no `title` (as abas: a regra de cada uma). */
  ajuda?: string;
}

/**
 * O seletor na LINHA FINA: o rótulo pequeno em cima, o valor em peso médio
 * sobre o fio, a seta desenhada (a seta é global, de `tokens.css`, e vale
 * para todo `<select>`).
 */
export function Seletor<T extends string>({
  rotulo,
  opcoes,
  valor,
  aoMudar,
  className,
  ...resto
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, "value" | "onChange"> & {
  rotulo?: string;
  opcoes: Opcao<T>[];
  valor: T;
  aoMudar: (v: T) => void;
}) {
  return (
    <label className={junta(css.campo, css.seletor, className)}>
      {rotulo && <span className={css.rotulo}>{rotulo}</span>}
      <select value={valor} onChange={(e) => aoMudar(e.currentTarget.value as T)} {...resto}>
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
            {o.conta !== undefined ? ` · ${o.conta}` : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A caixa de texto de várias linhas. Ela mantém a borda: um fio embaixo de
 * doze linhas não diz onde ela começa. */
export function AreaDeTexto({
  rotulo,
  className,
  id,
  ...resto
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { rotulo?: string }) {
  const proprio = useId();
  const chave = id ?? proprio;
  return (
    <label className={junta(css.campo, className)} htmlFor={chave}>
      {rotulo && <span className={css.rotulo}>{rotulo}</span>}
      <textarea id={chave} className={css.areaDeTexto} {...resto} />
    </label>
  );
}

/** O interruptor (liga e desliga), no lugar da caixinha de marcar. */
export function Interruptor({
  rotulo,
  descricao,
  className,
  ...resto
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  /** Sem rótulo, só a chave — quando o nome já está ao lado (a linha de
   * ajuste, que tem o `<label>` dela). */
  rotulo?: ReactNode;
  descricao?: ReactNode;
}) {
  return (
    <label className={junta(css.interruptorLinha, className)}>
      <input type="checkbox" role="switch" className={css.interruptor} {...resto} />
      {rotulo && (
        <span>
          <b>{rotulo}</b>
          {descricao && <span className={css.interruptorDescricao}> — {descricao}</span>}
        </span>
      )}
    </label>
  );
}

/* ═══ ESCOLHAS ══════════════════════════════════════════════════════════════ */

/**
 * A escolha em TEXTO: as opções lado a lado, separadas por um ponto, e a
 * escolhida em tinta cheia com o fio azul embaixo. Para escolher UMA entre
 * poucas (o estado de uma palavra, o filtro por estado).
 */
export function Escolha<T extends string>({
  opcoes,
  valor,
  aoMudar,
  rotulo,
  pequena,
  desligada,
}: {
  opcoes: Opcao<T>[];
  valor: T;
  aoMudar: (v: T) => void;
  /** O nome do grupo, para o leitor de tela. */
  rotulo: string;
  pequena?: boolean;
  desligada?: boolean;
}) {
  return (
    <div className={junta(css.escolha, pequena && css.escolhaPequena)} role="group" aria-label={rotulo}>
      {opcoes.map((o) => (
        <button key={o.valor} type="button" aria-pressed={valor === o.valor} disabled={desligada} title={o.ajuda}
                onClick={() => valor !== o.valor && aoMudar(o.valor)}>
          {o.rotulo}
          {o.conta !== undefined && <i>{o.conta}</i>}
        </button>
      ))}
    </div>
  );
}

/**
 * AS ABAS: uma linha com filete, a ativa sublinhada no azul, a contagem num
 * selo discreto — a vazia continua à vista, apagada: "zero aprovadas" é
 * informação. São as abas de Oportunidades e de Atestados.
 *
 * No desktop as que não cabem quebram de linha, inteiras. Até 980px é UMA
 * linha que rola para os lados, rente às bordas da tela, e a aba cortada na
 * borda é a pista de que há mais. Com `centraAtiva`, a ativa vem ao centro
 * dessa linha sempre que muda (e a rolagem assenta numa aba) — à mão e só no
 * eixo horizontal: `scrollIntoView` rolaria a página inteira.
 */
export function Abas<T extends string>({
  abas,
  ativa,
  aoMudar,
  rotulo,
  centraAtiva,
}: {
  abas: Opcao<T>[];
  ativa: T | null;
  aoMudar: (v: T) => void;
  rotulo: string;
  centraAtiva?: boolean;
}) {
  const barra = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = barra.current?.querySelector<HTMLElement>('button[aria-pressed="true"]');
    const b = barra.current;
    if (centraAtiva && b && el && b.scrollWidth > b.clientWidth) {
      b.scrollTo({ left: el.offsetLeft - (b.clientWidth - el.offsetWidth) / 2, behavior: "smooth" });
    }
  }, [ativa, centraAtiva]);
  return (
    <nav ref={barra} className={css.abas} aria-label={rotulo} data-centra={centraAtiva || undefined}>
      {abas.map((a) => (
        <button key={a.valor} type="button" aria-pressed={ativa === a.valor} title={a.ajuda}
                data-vazia={a.conta === 0} onClick={() => aoMudar(a.valor)}>
          {a.rotulo}
          {a.conta !== undefined && <i>{a.conta}</i>}
        </button>
      ))}
    </nav>
  );
}

/* ═══ MARCAS ════════════════════════════════════════════════════════════════ */

/** O número redondo em azul (filtros aplicados, encontradas). */
export function Conta({ children, rotulo }: { children: ReactNode; rotulo?: string }) {
  return <i className={css.conta} aria-label={rotulo}>{children}</i>;
}

/** O selo neutro de uma contagem ao lado de um título ("Avançado 76"). */
export function Selo({ children }: { children: ReactNode }) {
  return <i className={css.selo}>{children}</i>;
}

/** A etiqueta de assunto (uma linha de atuação, um termo). Não é estado.
 * `apagada` é o resto que não coube ("+3"). */
export function Etiqueta({ children, title, apagada }: { children: ReactNode; title?: string; apagada?: boolean }) {
  return <span className={junta(css.etiqueta, apagada && css.etiquetaApagada)} title={title}>{children}</span>;
}

export type TomDeEstado = "ok" | "atencao" | "perigo" | "andamento" | "neutro";

/** A pílula de ESTADO: o texto e a bolinha na cor dele (Concluída, Incompleta…). */
export function Pilula({ tom = "neutro", children, title }: { tom?: TomDeEstado; children: ReactNode; title?: string }) {
  return <span className={css.pilula} data-tom={tom} title={title}>{children}</span>;
}

/** O código de uma coisa (#201, a chave de uma configuração), em fonte fixa. */
export function Codigo({ children }: { children: ReactNode }) {
  return <code className={css.codigo}>{children}</code>;
}

/* ═══ AVISOS ════════════════════════════════════════════════════════════════ */

/**
 * O aviso: `info` (neutro, com o "i"), `atencao` e `erro`. Para o que a pessoa
 * precisa saber antes de seguir; não para enfeitar.
 *
 * `simples` é a linha de estado, sem ícone: "Carregando…", "Nada passou pelos
 * filtros", a mensagem de erro do servidor. Sem o ícone, o tom vai para o
 * texto — o erro sai inteiro em vermelho.
 */
export function Aviso({ tom = "info", titulo, simples, children }: {
  tom?: "info" | "atencao" | "erro";
  titulo?: ReactNode;
  simples?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={css.aviso} data-tom={tom} data-simples={simples || undefined}
         role={tom === "erro" ? "alert" : undefined}>
      {!simples && (tom === "info" ? <IconeInfo /> : <IconeAlerta />)}
      <p>
        {titulo && <b>{titulo} </b>}
        {children}
      </p>
    </div>
  );
}

/** O estado vazio: o que falta e o que fazer, com o tracejado de "nada aqui".
 * Sem título, é só a frase ("Lendo o acervo…"). */
export function Vazio({ titulo, children }: { titulo?: ReactNode; children?: ReactNode }) {
  return (
    <div className={css.vazio}>
      {titulo && <b>{titulo}</b>}
      {children && <p>{children}</p>}
    </div>
  );
}

/* ═══ ESTRUTURA ═════════════════════════════════════════════════════════════ */

/** O cabeçalho de uma tela: o título de 28px, a frase e as ações à direita. */
export function Cabecalho({ titulo, descricao, acoes }: {
  titulo: ReactNode;
  descricao?: ReactNode;
  acoes?: ReactNode;
}) {
  return (
    <header className={css.cabecalho}>
      <div>
        <h1>{titulo}</h1>
        {descricao && <p>{descricao}</p>}
      </div>
      {acoes && <div className={css.cabecalhoAcoes}>{acoes}</div>}
    </header>
  );
}

/** O texto de apoio de uma seção: a frase que explica o que vem embaixo (ou o
 * que se vê em cima). Cinza, 14px, até 80 caracteres por linha. */
export function Nota({ children }: { children: ReactNode }) {
  return <p className={css.secaoNota}>{children}</p>;
}

/**
 * Uma seção da tela: o título (com contagem, se houver), a nota e o conteúdo.
 * O título dá nome à seção para o leitor de tela; sem título, o `rotulo` dá.
 */
export function Secao({ titulo, conta, nota, acoes, rotulo, children }: {
  titulo?: ReactNode;
  conta?: number;
  nota?: ReactNode;
  acoes?: ReactNode;
  /** O nome da seção que não tem título à vista (o resultado de uma busca). */
  rotulo?: string;
  children?: ReactNode;
}) {
  const id = useId();
  return (
    <section className={css.secao} aria-labelledby={titulo ? id : undefined}
             aria-label={titulo ? undefined : rotulo}>
      {(titulo || acoes) && (
        <div className={css.secaoCabeca}>
          {titulo && (
            <h2 id={id}>
              {titulo}
              {conta !== undefined && <Selo>{conta.toLocaleString("pt-BR")}</Selo>}
            </h2>
          )}
          {acoes}
        </div>
      )}
      {nota && <Nota>{nota}</Nota>}
      {children}
    </section>
  );
}

/** O respiro de dentro de um cartão ou de uma dobra: o de sempre; `linhas`,
 * só dos lados, para uma pilha de linhas de ajuste (cada uma já traz o seu em
 * cima e embaixo); `solto`, nenhum. */
const corpo = (base: string | undefined, linhas?: boolean, solto?: boolean) =>
  solto ? undefined : junta(base, linhas && css.corpoDeLinhas);

/** O cartão: superfície sólida, borda fina, o canto do sistema. Com `titulo`,
 * ganha a faixa de cima com o título e as ações. */
export function Cartao({ titulo, acoes, solto, linhas, children, className }: {
  titulo?: ReactNode;
  acoes?: ReactNode;
  /** Sem o respiro de dentro (para listas e linhas que já têm o seu). */
  solto?: boolean;
  /** O corpo é uma pilha de `LinhaDeAjuste`. */
  linhas?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={junta(css.cartao, className)}>
      {titulo && (
        <div className={css.cartaoCabeca}>
          <h3>{titulo}</h3>
          {acoes}
        </div>
      )}
      <div className={corpo(css.cartaoCorpo, linhas, solto)}>{children}</div>
    </div>
  );
}

/** A seção que abre e fecha (o "Avançado", o "Como funciona"). Os filhos do
 * corpo ficam a 12px um do outro. */
export function Dobra({ titulo, resumo, children, aberta, solto, linhas }: {
  titulo: ReactNode;
  resumo?: ReactNode;
  children: ReactNode;
  aberta?: boolean;
  /** Sem o respiro de dentro (o conteúdo traz o seu, como os subgrupos). */
  solto?: boolean;
  /** O corpo é uma pilha de `LinhaDeAjuste`. */
  linhas?: boolean;
}) {
  return (
    <details className={css.dobra} open={aberta}>
      <summary>
        {titulo}
        {resumo && <small>{resumo}</small>}
        <IconeChevron className={css.dobraSeta} />
      </summary>
      <div className={corpo(css.dobraCorpo, linhas, solto)}>{children}</div>
    </details>
  );
}

/**
 * A linha de ajuste: o nome e o que ele muda à esquerda, o controle à direita
 * (as configurações). No celular o controle desce para baixo do texto.
 *
 * Dentro dela o campo tem a largura do valor: o número 110px, o texto 280px,
 * o seletor até 280px. `largo` é para o texto de várias linhas: o controle
 * desce para baixo do nome e ocupa a largura toda.
 */
export function LinhaDeAjuste({ nome, para, explica, mais, rodape, erro, largo, children }: {
  nome: ReactNode;
  /** O `id` do controle: o nome vira o `<label>` dele. */
  para?: string;
  explica?: ReactNode;
  /** O texto longo, recolhido num "Mais detalhes". */
  mais?: ReactNode;
  /** A linha miúda embaixo ("Alterado por…"). */
  rodape?: ReactNode;
  /** O erro, na largura toda, embaixo de tudo. */
  erro?: ReactNode;
  largo?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={junta(css.ajuste, largo && css.ajusteLargo)}>
      <div className={css.ajusteTexto}>
        {para ? (
          <label htmlFor={para} className={css.ajusteNome}>{nome}</label>
        ) : (
          <span className={css.ajusteNome}>{nome}</span>
        )}
        {explica && <p className={css.ajusteExplica}>{explica}</p>}
        {mais && (
          <details className={css.ajusteMais}>
            <summary>Mais detalhes</summary>
            <p className={css.ajusteExplica}>{mais}</p>
          </details>
        )}
        {rodape && <p className={css.ajusteRodape}>{rodape}</p>}
      </div>
      <div className={css.ajusteControle}>{children}</div>
      {erro && <div className={css.ajusteErro}>{erro}</div>}
    </div>
  );
}

/** A lista de linhas: uma caixa com filetes entre os itens. */
export function Lista({ children, rotulo }: { children: ReactNode; rotulo?: string }) {
  return <ul className={css.lista} aria-label={rotulo}>{children}</ul>;
}

/**
 * Uma linha da lista: o começo (uma pílula, um prazo), o corpo e o fim
 * (números). O que vier como filho entra no corpo, depois da meta (as
 * etiquetas, um alerta, o erro).
 *
 * No celular o fim desce para uma linha própria, encostado à esquerda; quem
 * quiser alinhá-lo com o texto dá o recuo em `--recuo-do-fim` (a Fila, com o
 * bloco do prazo na frente).
 */
export function ItemDeLista({ inicio, titulo, texto, meta, fim, href, externo, title, className, children }: {
  inicio?: ReactNode;
  titulo?: ReactNode;
  texto?: ReactNode;
  meta?: ReactNode;
  fim?: ReactNode;
  /** Com endereço, a linha inteira é o link. */
  href?: string;
  /** O link abre noutra aba (o anúncio no portal de origem). */
  externo?: boolean;
  title?: string;
  className?: string;
  children?: ReactNode;
}) {
  const dentro = (
    <>
      {inicio}
      <span className={css.itemCorpo}>
        {titulo && <span className={css.itemTitulo}>{titulo}</span>}
        {texto && <span className={css.itemTexto}>{texto}</span>}
        {meta && <span className={css.itemMeta}>{meta}</span>}
        {children}
      </span>
      {fim && <span className={css.itemFim}>{fim}</span>}
    </>
  );
  const classe = junta(css.item, className);
  return (
    <li>
      {href ? (
        <a className={classe} href={href} title={title}
           {...(externo ? { target: "_blank", rel: "noreferrer" } : {})}>
          {dentro}
        </a>
      ) : (
        <div className={classe} title={title}>{dentro}</div>
      )}
    </li>
  );
}

/* ═══ NÚMEROS ═══════════════════════════════════════════════════════════════ */

/**
 * As cifras: números grandes e finos, o nome embaixo, sem caixa; o zero
 * apagado. A `ajuda` é o `title` onde há mouse; no toque, que não tem `title`,
 * ela aparece escrita embaixo do nome.
 */
export function Cifras({ itens }: { itens: { rotulo: string; valor: number | string; ajuda?: string }[] }) {
  return (
    <dl className={css.cifras}>
      {itens.map((c) => (
        <div key={c.rotulo} title={c.ajuda} data-zero={c.valor === 0} data-ajuda={!!c.ajuda || undefined}>
          <dt>{c.rotulo}</dt>
          <dd>
            {typeof c.valor === "number" ? c.valor.toLocaleString("pt-BR") : c.valor}
            {c.ajuda && <span className={css.ajudaToque}>{c.ajuda}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** A barra proporcional fina, com o número na ponta. `fracao` de 0 a 1. */
export function Barra({ fracao, valor }: { fracao: number; valor?: ReactNode }) {
  return (
    <span className={css.barra}>
      <i style={{ width: `calc((100% - 64px) * ${Math.max(0, Math.min(1, fracao))})` }} />
      {valor !== undefined && <b>{valor}</b>}
    </span>
  );
}
